import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import CtaBlock from "../components/CtaBlock";
import SEOMeta from "../components/SEOMeta";

export default function InsightArticle() {
  return (
    <div data-testid="insight-article-page">
      <SEOMeta
        title="The Sovereign Mineral Question"
        description="Dugavu Ventures examines the sovereign mineral question — how corporate strategy and state ownership intersect in Africa's mining and minerals sector, and what it means for oil and gas governance."
        url="/insights/sovereign-mineral-strategy"
      />
      <article className="pt-20 md:pt-28">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <Link
            to="/insights"
            data-testid="back-to-insights"
            className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase font-mono text-slate hover:text-oxblood transition-colors"
          >
            <ArrowLeft size={14} weight="thin" />
            Back to insights
          </Link>

          <header className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 pb-12 border-b border-rule">
            <div className="md:col-span-3">
              <div className="eyebrow">Strategy note</div>
              <div className="mt-6 text-[12px] tracking-widest uppercase font-mono text-slate">Mining and minerals</div>
              <div className="mt-2 text-[12px] tracking-widest uppercase font-mono text-slate">12 min read</div>
            </div>
            <div className="md:col-span-9">
              <h1 className="display-1 text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-ink leading-[1.05]">
                The sovereign mineral question: when state ownership is the strategy.
              </h1>
              <p className="lede mt-8 text-xl md:text-2xl text-charcoal max-w-3xl">
                Less a question of nationalisation, more a question of institutional design.
              </p>
              <div className="mt-10 text-[12px] tracking-widest uppercase font-mono text-slate">
                By Dugavu Ventures
              </div>
            </div>
          </header>
        </div>
      </article>

      <section className="py-16 md:py-20">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            <aside className="md:col-span-3">
              <div className="md:sticky md:top-28">
                <div className="text-[11px] tracking-widest uppercase font-mono text-slate mb-4">Contents</div>
                <ol className="space-y-3 text-[13px]">
                  <li><a href="#section-1" className="text-charcoal hover:text-oxblood link-underline">01 — The wrong frame</a></li>
                  <li><a href="#section-2" className="text-charcoal hover:text-oxblood link-underline">02 — What ownership is for</a></li>
                  <li><a href="#section-3" className="text-charcoal hover:text-oxblood link-underline">03 — The institutional question</a></li>
                  <li><a href="#section-4" className="text-charcoal hover:text-oxblood link-underline">04 — Three design choices</a></li>
                  <li><a href="#section-5" className="text-charcoal hover:text-oxblood link-underline">05 — A note on capacity</a></li>
                </ol>
              </div>
            </aside>

            <div className="md:col-span-9 max-w-3xl">
              <div className="prose-custom space-y-7 text-[17px] text-charcoal leading-[1.85] font-serif">
                <p className="text-2xl md:text-3xl text-ink leading-snug font-serif italic">
                  The debate about state ownership in Africa's mineral sectors is, in our view, asking the wrong question. The right question is not whether the state should own — it is what the state intends ownership to do.
                </p>

                <section id="section-1">
                  <h2 className="font-serif text-3xl md:text-4xl text-ink mt-12 mb-5 not-italic">01 — The wrong frame</h2>
                  <p>
                    Most public discussion of state ownership in mining proceeds as if ownership itself were the strategy. It is not. Ownership is an instrument. The strategy lies in what the instrument is being used to achieve, and whether the institutional arrangements behind it are capable of delivering on the intent.
                  </p>
                  <p>
                    Where state ownership has worked — in places as different as Botswana, Norway, and parts of the Gulf — it has done so because ownership was matched to a clear mandate, capable institutions, and disciplined commercial governance. Where it has failed, the failure is rarely about ownership in the abstract. It is about the institutional design behind it.
                  </p>
                </section>

                <section id="section-2">
                  <h2 className="font-serif text-3xl md:text-4xl text-ink mt-12 mb-5 not-italic">02 — What ownership is for</h2>
                  <p>
                    State ownership in mineral sectors is typically pursued for one or more of four reasons: capturing economic rent that would otherwise leave the country, building national industrial capacity around the resource, securing strategic supply for downstream industries, and embedding development outcomes that markets alone will not deliver.
                  </p>
                  <p>
                    These are not equivalent objectives, and the institutional design that serves one well may serve another poorly. A state mining company structured to maximise rent capture looks different from one structured to anchor downstream beneficiation. Confusion between the two is one of the more common failure modes.
                  </p>
                </section>

                <section id="section-3">
                  <h2 className="font-serif text-3xl md:text-4xl text-ink mt-12 mb-5 not-italic">03 — The institutional question</h2>
                  <p>
                    Once the purpose of ownership is clear, the institutional question becomes tractable. It has three parts: mandate, governance, and capability.
                  </p>
                  <p>
                    Mandate must be legally precise and politically protected. A state mining entity cannot reasonably be asked to deliver commercial returns, social outcomes, regional development, and strategic stockpiling all at once — and certainly not without explicit ranking. The mandate must say what the institution is for, and what it is not for.
                  </p>
                  <p>
                    Governance must allow the institution to act commercially within that mandate. Boards must have authority. Performance contracts must measure what the mandate prioritises. The supervising ministry must hold the institution to account without operating it day-to-day.
                  </p>
                  <p>
                    Capability — technical, commercial, and managerial — must be built deliberately. It does not arrive with the legislation that creates the entity. It is the result of recruitment, retention, partnership, and time.
                  </p>
                </section>

                <section id="section-4">
                  <h2 className="font-serif text-3xl md:text-4xl text-ink mt-12 mb-5 not-italic">04 — Three design choices</h2>
                  <p>
                    Three design choices, in our experience, separate effective state mineral institutions from less effective ones.
                  </p>
                  <p>
                    The first is the boundary between policy and operations. Where regulatory authority and operating responsibility sit in the same institution, neither tends to be done well. Separation, with clear interfaces, is almost always preferable.
                  </p>
                  <p>
                    The second is the use of joint ventures. Holding a minority stake alongside a competent operator is, in many cases, a better way to participate in upside while building capability than holding a majority stake in an institution that lacks the experience to operate. The choice is not abstract — it depends on the asset, the partners available, and the mandate of the state entity.
                  </p>
                  <p>
                    The third is the discipline around capital. State mineral institutions that retain earnings without a clear capital allocation framework drift over time. Those with explicit dividend policies, investment criteria, and treasury arrangements tend to remain commercially anchored.
                  </p>
                </section>

                <section id="section-5">
                  <h2 className="font-serif text-3xl md:text-4xl text-ink mt-12 mb-5 not-italic">05 — A note on capacity</h2>
                  <p>
                    None of this should be taken as an argument against state ownership. The argument is for institutional seriousness about it. Ownership without mandate is a vehicle without a destination. Mandate without governance is a destination without a route. Governance without capability is a route without a vehicle.
                  </p>
                  <p>
                    The work of building capable mineral institutions is patient work. It is the work of mandate review, board design, performance frameworks, capital strategy, and the slow accumulation of commercial judgement inside the institution. There is no policy shortcut to it. There is no consultancy shortcut to it either. There is only the work.
                  </p>
                  <p className="text-ink italic">
                    For governments and state mineral entities working on this question, we are available to discuss specific mandates.
                  </p>
                </section>
              </div>

              {/* Footer */}
              <div className="mt-16 pt-10 border-t border-rule grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-[11px] tracking-widest uppercase font-mono text-slate">Authored by</div>
                  <div className="font-serif text-2xl text-ink mt-2">Dugavu Ventures</div>
                  <div className="text-[13px] text-slate mt-1">Strategy advisory · Kampala</div>
                </div>
                <div className="sm:text-right">
                  <a
                    href="mailto:mandates@dugavuventures.com?subject=Sovereign mineral strategy"
                    data-testid="article-cta"
                    className="inline-flex items-center gap-3 bg-oxblood text-white px-6 py-4 text-[11px] tracking-widest uppercase font-medium hover:bg-oxblood-hover transition-colors duration-300"
                  >
                    Discuss this note
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBlock />
    </div>
  );
}
